import React from 'react';
import Sidebar from './SidebarNew';
import TopBar from './TopBar';
import MobileNavigation from './MobileNavigation';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, Clock, XCircle } from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  
  // Check if vendor account is pending approval
  const showPendingBanner = user?.role === 'vendor' && user?.status === 'pending';
  const showSuspendedBanner = user?.role === 'vendor' && user?.status === 'suspended';
  const showRejectedBanner = user?.role === 'vendor' && user?.status === 'rejected';
  
  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      
      {/* Desktop TopBar - Hidden on mobile */}
      <div className="hidden lg:block">
        <TopBar />
      </div>
      
      {/* Mobile Header - Only visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 z-30 flex items-center px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span className="text-lg font-bold text-gray-900">Aniyor</span>
        </div>
      </div>
      
      {/* Main content area - adjusted for desktop sidebar/topbar and mobile header */}
      <div className="flex-1 lg:ml-64 lg:mt-16 mt-14 mb-20 lg:mb-0 overflow-x-hidden">
        {/* Account Status Banners */}
        {showPendingBanner && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">Account Pending Approval</p>
                <p className="text-amber-700 text-sm">
                  Your vendor account is under review. You can set up your store and create listings, but publishing will be enabled once an admin approves your account.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {showSuspendedBanner && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Account Suspended</p>
                <p className="text-red-700 text-sm">
                  Your account has been suspended. Please contact support for more information.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {showRejectedBanner && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Account Rejected</p>
                <p className="text-red-700 text-sm">
                  Your vendor registration was not approved. Please contact support for assistance.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile padding for bottom nav, desktop padding normal */}
        <div className="p-4 lg:p-8 max-w-full">
          {children}
        </div>
      </div>
      
      {/* Mobile Navigation - Only visible on mobile */}
      <MobileNavigation />
    </div>
  );
};

export default DashboardLayout;